import type { CharacterConfig } from '../config/character.config';
import type { DeathReason } from '../core/GameEvents';
import { PlayerState } from '../player/PlayerState';
import { clamp } from '../utils/math';
import { ALL_CHARACTER_CLIPS, CharacterClip, ClipLayer } from './CharacterTypes';

/**
 * Pure mapping gameplay → animation state (no Babylon): which clip the character
 * shows for a player state, how one-shot reactions are layered on top and how
 * fast the run cycle plays. Unit-tested; CharacterAnimator only executes it.
 */
export interface AnimationContext {
  readonly state: PlayerState;
  /** A run is in progress (otherwise the character idles on the start screen). */
  readonly running: boolean;
  /** Difficulty speed 0..1. */
  readonly normalizedSpeed: number;
  /** Why the run ended – chooses the end pose. */
  readonly deathReason: DeathReason | null;
  /** The extra-life drink is on (DrinkBreak) – it replaces every other clip. */
  readonly drinking?: boolean;
  /** The current hit happened in the air (a jump onto something did not make it) – trip instead of crash. */
  readonly hitTrip?: boolean;
}

/**
 * Node of a character rig the drink bottle hangs on (tools/blender/lib/hand_prop.py): parented to the right
 * hand, origin = the bottle's bottom centre, +y = the bottle axis – the collectible's own frame.
 */
export const BOTTLE_ANCHOR_NODE = 'PROP_BottleAnchor';

/**
 * Scale of the drink bottle at this point of the drink (progress 0..1 of the clip): 0 outside the bottle frames,
 * popping in past full size (overshoot) and settling, then shrinking away at the end.
 */
export function drinkBottleScale(progress: number, config: CharacterConfig): number {
  const { clipFrames, clipFps, bottleFrames, bottle } = config.drinkBreak;
  const frame = 1 + progress * (clipFrames - 1);
  if (frame < bottleFrames[0] || frame > bottleFrames[1]) return 0;
  const sinceIn = (frame - bottleFrames[0]) / clipFps;
  const untilOut = (bottleFrames[1] - frame) / clipFps;
  if (untilOut < bottle.popOutSeconds) return untilOut / bottle.popOutSeconds;
  if (sinceIn >= bottle.popInSeconds) return 1;
  // pop: 0 → 1 + overshoot at two thirds of the pop, back to 1
  const t = sinceIn / bottle.popInSeconds;
  return t < 2 / 3 ? (1 + bottle.overshoot) * (t * 1.5) : 1 + bottle.overshoot * (1 - (t - 2 / 3) * 3);
}

const LOOPING: ReadonlySet<CharacterClip> = new Set([
  CharacterClip.Idle,
  CharacterClip.Run,
  CharacterClip.RunFast,
  CharacterClip.Fall,
  CharacterClip.Drive,
]);

const UPPER_BODY: ReadonlySet<CharacterClip> = new Set([
  CharacterClip.LaneLeft,
  CharacterClip.LaneRight,
  CharacterClip.Collect,
  CharacterClip.Celebrate,
]);

/** Quick reactions get the short cross-fade. */
const SNAPPY: ReadonlySet<CharacterClip> = new Set([
  CharacterClip.Jump,
  CharacterClip.DoubleJump,
  CharacterClip.Slide,
  CharacterClip.Hit,
  CharacterClip.HitTrip,
  CharacterClip.Land,
  CharacterClip.StumbleLeft,
  CharacterClip.StumbleRight,
]);

/** Looping clips repeat; all others play once and hold their last frame (or return to the base clip if layered). */
export function isLooping(clip: CharacterClip): boolean {
  return LOOPING.has(clip);
}

/**
 * The clip shown where a model lacks `clip`: resting poses (Idle, the end poses, seated on the dozer) fall back to
 * Idle, everything in motion to the run cycle.
 */
export function clipFallback(clip: CharacterClip): CharacterClip {
  const resting = clip === CharacterClip.Idle || clip === CharacterClip.GameOver || clip === CharacterClip.Caught || clip === CharacterClip.Drive;
  return resting ? CharacterClip.Idle : CharacterClip.Run;
}

/** Run cycles: both start with the left foot touching down, so a switch can keep the step phase. */
export function isRunCycle(clip: CharacterClip): boolean {
  return clip === CharacterClip.Run || clip === CharacterClip.RunFast;
}

export function layerOf(clip: CharacterClip): ClipLayer {
  return UPPER_BODY.has(clip) ? ClipLayer.UpperBody : ClipLayer.FullBody;
}

/** Bones (without instance prefix) a layered clip drives; unknown layered clips drive the torso. */
export function maskOf(clip: CharacterClip, config: CharacterConfig): readonly string[] {
  const masks: Readonly<Partial<Record<CharacterClip, readonly string[]>>> = config.clipMasks;
  return masks[clip] ?? config.clipMasks.LaneLeft;
}

/**
 * Bones driven by the same set of layered clips (torso: lean, pickup, cheer – right arm: pickup, cheer –
 * …). Full-body clips are split along these regions so a layered clip can push the base clip back
 * exactly where it plays (Babylon caps every animation weight at 1, a heavier layer is impossible).
 */
export interface BodyRegion {
  /** Bone names; null = every bone no layered clip drives (hips, legs, root, sockets). */
  readonly bones: ReadonlySet<string> | null;
  /** Layered clips that cover this region. */
  readonly layers: ReadonlySet<CharacterClip>;
}

export function bodyRegions(config: CharacterConfig): BodyRegion[] {
  const layered = ALL_CHARACTER_CLIPS.filter((clip) => layerOf(clip) === ClipLayer.UpperBody);
  const coveredBy = new Map<string, CharacterClip[]>();
  for (const clip of layered) {
    for (const bone of maskOf(clip, config)) coveredBy.set(bone, [...(coveredBy.get(bone) ?? []), clip]);
  }
  const regions = new Map<string, { bones: Set<string>; layers: ReadonlySet<CharacterClip> }>();
  for (const [bone, clips] of coveredBy) {
    const key = clips.join('|');
    const region = regions.get(key) ?? { bones: new Set<string>(), layers: new Set(clips) };
    region.bones.add(bone);
    regions.set(key, region);
  }
  return [...regions.values(), { bones: null, layers: new Set<CharacterClip>() }];
}

/** Share (0..1) the base clip keeps in a region while layered clips with the given weights cover it. */
export function baseShare(coverWeight: number, config: CharacterConfig): number {
  return 1 - config.overlayDominance * clamp(coverWeight, 0, 1);
}

export function fadeSecondsFor(clip: CharacterClip, config: CharacterConfig): number {
  return SNAPPY.has(clip) ? config.quickFadeSeconds : config.fadeSeconds;
}

/** The continuous clip for the current player state. */
export function baseClipFor(ctx: AnimationContext, config: CharacterConfig): CharacterClip {
  if (ctx.state === PlayerState.Dead) return ctx.deathReason === 'CAUGHT' ? CharacterClip.Caught : CharacterClip.GameOver;
  if (!ctx.running) return CharacterClip.Idle;
  if (ctx.drinking) return CharacterClip.Drink;
  switch (ctx.state) {
    case PlayerState.Jumping:
      return CharacterClip.Jump;
    case PlayerState.DoubleJumping:
      return CharacterClip.DoubleJump;
    case PlayerState.Falling:
      return CharacterClip.Fall;
    case PlayerState.Landing:
      return CharacterClip.Land;
    case PlayerState.Sliding:
      return CharacterClip.Slide;
    case PlayerState.Hit:
      return ctx.hitTrip ? CharacterClip.HitTrip : CharacterClip.Hit;
    case PlayerState.Running:
    case PlayerState.Stumbling:
      return ctx.normalizedSpeed >= config.runFastFrom ? CharacterClip.RunFast : CharacterClip.Run;
    case PlayerState.Driving:
      return CharacterClip.Drive;
  }
}

/** Lane indices grow to the right (MoveLeft = −1). */
export function laneChangeClip(fromLane: number, toLane: number): CharacterClip {
  return toLane < fromLane ? CharacterClip.LaneLeft : CharacterClip.LaneRight;
}

/** Stumbles throw the body away from the side movement; straight on alternates. */
export function stumbleClip(lateralVelocity: number, previous: CharacterClip | null): CharacterClip {
  if (lateralVelocity < 0) return CharacterClip.StumbleRight;
  if (lateralVelocity > 0) return CharacterClip.StumbleLeft;
  return previous === CharacterClip.StumbleLeft ? CharacterClip.StumbleRight : CharacterClip.StumbleLeft;
}

/** Moments worth a cheer (upper-body fist pump while running). */
export type CheerCause = 'extraLife' | 'bonus' | 'milestone';

/** A pickup worth at least `bonusFromBottleValue` bottles makes the runner cheer instead of just grabbing. */
export function isCheerPickup(bottleValue: number, config: CharacterConfig): boolean {
  return bottleValue >= config.cheer.bonusFromBottleValue;
}

/** True when the run crossed a multiple of `milestoneMeters` between two distances. */
export function crossedMilestone(previousDistance: number, distance: number, config: CharacterConfig): boolean {
  const every = config.cheer.milestoneMeters;
  if (every <= 0 || distance <= previousDistance) return false;
  return Math.floor(distance / every) > Math.floor(previousDistance / every);
}

/** Cheering only looks right on the run – airborne, sliding or hit states keep it pending. */
export function canCheer(state: PlayerState): boolean {
  return state === PlayerState.Running;
}

/** Playback speed of the run cycles so the feet keep up with the run speed. */
export function runSpeedRatio(clip: CharacterClip, speed: number, config: CharacterConfig): number {
  const reference = clip === CharacterClip.RunFast ? config.runFastClipSpeed : clip === CharacterClip.Run ? config.runClipSpeed : 0;
  if (reference <= 0) return 1;
  return clamp(speed / reference, config.runSpeedRatio.min, config.runSpeedRatio.max);
}
